/**
 * GIS レイヤー既定スタイル編集フォーム (docs/22 要件3、Sprint G2)。
 * 線/面の色・透明度・線幅・フォントサイズ・ラベル属性を geometry 種別に応じて編集する。
 * 親 (GisAdminPage) が保存後の onSnapshot 更新でプレビューに反映する。
 */

import { Button, Checkbox, Select } from '@citizen-walk/shared-ui';
import { useState } from 'react';
import type {
  GisCategoricalStyle,
  GisCategory,
  GisLayer,
  GisLayerStyle,
} from '@citizen-walk/shared-types';
import { ColorSwatch } from '../masters';

interface Props {
  layer: GisLayer;
  busy: boolean;
  onSave: (style: GisLayerStyle) => void;
}

export function GisLayerStyleEditor({ layer, busy, onSave }: Props) {
  const [draft, setDraft] = useState<GisLayerStyle>(layer.style);
  const set = <K extends keyof GisLayerStyle>(key: K, value: GisLayerStyle[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // 属性値別の色分け (categorical) 編集ヘルパー (G7)。
  const cat = draft.categorical;
  const setCategorical = (next: GisCategoricalStyle | undefined) => set('categorical', next);
  const updateCat = (i: number, patch: Partial<GisCategory>) => {
    if (!cat) return;
    setCategorical({
      ...cat,
      categories: cat.categories.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    });
  };
  const enableCategorical = (on: boolean) =>
    setCategorical(
      on
        ? (cat ?? {
            field: layer.attributeFields[0]?.key ?? '',
            categories: [],
            defaultColor: '#9ca3af',
          })
        : undefined,
    );

  const gt = layer.geometryType;
  const showFill = gt === 'polygon' || gt === 'mixed';
  const showLine = gt === 'line' || gt === 'polygon' || gt === 'mixed';
  const showPoint = gt === 'point' || gt === 'mixed';
  const showArrow = gt === 'line' || gt === 'mixed'; // 方向矢印は線のみ (QGIS ArrowLine 相当)

  const dirty = JSON.stringify(draft) !== JSON.stringify(layer.style);

  return (
    <div className="grid grid-cols-1 gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      {showFill && (
        <Section title="面 (塗り)">
          <ColorRow label="塗り色" value={draft.fillColor} onChange={(v) => set('fillColor', v)} />
          <RangeRow
            label="塗り不透明度"
            value={draft.fillOpacity}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => set('fillOpacity', v)}
          />
        </Section>
      )}
      {showLine && (
        <Section title="線">
          <ColorRow label="線色" value={draft.lineColor} onChange={(v) => set('lineColor', v)} />
          <RangeRow
            label="線の太さ"
            value={draft.lineWidth}
            min={0}
            max={12}
            step={0.5}
            onChange={(v) => set('lineWidth', v)}
          />
          <RangeRow
            label="線の不透明度"
            value={draft.lineOpacity}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => set('lineOpacity', v)}
          />
        </Section>
      )}
      {showPoint && (
        <Section title="点 (円)">
          <ColorRow
            label="円の色"
            value={draft.circleColor}
            onChange={(v) => set('circleColor', v)}
          />
          <RangeRow
            label="半径"
            value={draft.circleRadius}
            min={1}
            max={24}
            step={1}
            onChange={(v) => set('circleRadius', v)}
          />
          <RangeRow
            label="不透明度"
            value={draft.circleOpacity}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => set('circleOpacity', v)}
          />
          <ColorRow
            label="縁色"
            value={draft.circleStrokeColor}
            onChange={(v) => set('circleStrokeColor', v)}
          />
          <RangeRow
            label="縁の太さ"
            value={draft.circleStrokeWidth}
            min={0}
            max={6}
            step={0.5}
            onChange={(v) => set('circleStrokeWidth', v)}
          />
        </Section>
      )}
      {showArrow && (
        <Section title="方向矢印 (線)">
          <Checkbox
            label="線に沿って方向矢印を描く"
            checked={draft.arrowEnabled === true}
            onChange={(c) => set('arrowEnabled', c)}
          />
          {draft.arrowEnabled && (
            <>
              <p className="text-[11px] text-slate-500">
                QGIS の矢印シンボル相当。矢じりを線方向に沿って繰り返し描きます。
              </p>
              <ColorRow
                label="矢印色"
                value={draft.arrowColor ?? draft.lineColor}
                onChange={(v) => set('arrowColor', v)}
              />
              <RangeRow
                label="矢じりサイズ"
                value={draft.arrowSize ?? 6}
                min={2}
                max={20}
                step={1}
                onChange={(v) => set('arrowSize', v)}
              />
            </>
          )}
        </Section>
      )}

      <Section title="ラベル">
        <Checkbox
          label="属性をラベル表示する"
          checked={draft.labelEnabled}
          onChange={(c) => set('labelEnabled', c)}
        />
        {draft.labelEnabled && (
          <>
            <Select
              label="表示する属性"
              value={draft.labelField ?? ''}
              onChange={(v) => set('labelField', v === '' ? undefined : v)}
              options={[
                { value: '', label: '(なし)' },
                ...layer.attributeFields.map((f) => ({ value: f.key, label: f.key })),
              ]}
            />
            <RangeRow
              label="フォントサイズ"
              value={draft.labelFontSize}
              min={8}
              max={28}
              step={1}
              onChange={(v) => set('labelFontSize', v)}
            />
            <ColorRow
              label="文字色"
              value={draft.labelColor}
              onChange={(v) => set('labelColor', v)}
            />
            <ColorRow
              label="縁取り色"
              value={draft.labelHaloColor}
              onChange={(v) => set('labelHaloColor', v)}
            />
          </>
        )}
      </Section>

      <Section title="属性値で色分け (categorized)">
        <Checkbox label="属性値ごとに色を変える" checked={!!cat} onChange={enableCategorical} />
        {cat && (
          <>
            <p className="text-[11px] text-slate-500">
              ON のときは各値の色が上の単色設定より優先されます (不透明度・太さ・ラベルは共通)。
            </p>
            <Select
              label="色分けに使う属性"
              value={cat.field}
              onChange={(v) => setCategorical({ ...cat, field: v })}
              options={layer.attributeFields.map((f) => ({ value: f.key, label: f.key }))}
            />
            <ColorRow
              label="未一致 / NULL の色"
              value={cat.defaultColor}
              onChange={(v) => setCategorical({ ...cat, defaultColor: v })}
            />
            <div className="grid grid-cols-1 gap-1.5">
              <p className="text-xs font-semibold text-slate-600">
                値ごとの色 ({cat.categories.length})
              </p>
              {cat.categories.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={c.value}
                    onChange={(e) => updateCat(i, { value: e.target.value })}
                    placeholder="属性値"
                    className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-800"
                  />
                  <ColorSwatch
                    value={c.color}
                    onChange={(next) => updateCat(i, { color: next ?? c.color })}
                    showClear={false}
                    size={24}
                    ariaLabel={`値 ${c.value || i + 1} の色`}
                  />
                  <button
                    type="button"
                    aria-label="この値を削除"
                    onClick={() =>
                      setCategorical({
                        ...cat,
                        categories: cat.categories.filter((_, idx) => idx !== i),
                      })
                    }
                    className="shrink-0 rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
                  >
                    ×
                  </button>
                </div>
              ))}
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  setCategorical({
                    ...cat,
                    categories: [...cat.categories, { value: '', color: '#3b82f6' }],
                  })
                }
              >
                値を追加
              </Button>
            </div>
          </>
        )}
      </Section>

      <div className="flex items-center justify-end gap-2">
        {dirty && <span className="text-xs text-amber-600">未保存の変更</span>}
        <Button
          size="sm"
          variant="secondary"
          disabled={!dirty || busy}
          onClick={() => setDraft(layer.style)}
        >
          リセット
        </Button>
        <Button size="sm" disabled={!dirty || busy} onClick={() => onSave(draft)}>
          スタイルを保存
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1.5">
      <p className="text-xs font-semibold text-slate-700">{title}</p>
      {children}
    </div>
  );
}

function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-slate-600">{label}</span>
      <span className="flex items-center gap-2">
        <span className="text-xs font-mono text-slate-500">{value}</span>
        <ColorSwatch
          value={value}
          onChange={(c) => onChange(c ?? value)}
          showClear={false}
          size={24}
          ariaLabel={`${label} の色`}
        />
      </span>
    </div>
  );
}

function RangeRow({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-slate-600">{label}</span>
      <span className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-28"
        />
        <span className="w-8 text-right text-xs font-mono text-slate-500">{value}</span>
      </span>
    </div>
  );
}
